import React, { Fragment, useEffect, useReducer, useState, useMemo } from 'react';
import { compose, withProps } from 'recompose';

import GraphQLCRUD from '@/se/components/GraphQLCRUD';
import EntityEmptyState from '@/se/components/entity-states/EntityEmptyState';

import get from 'lodash/get';
import { get as get2 } from 'lodash/fp';

import { createdProcedureColumns, listColumns, uploadedProcedureColumns } from './columns';
import schema, {
  procedureStatistics,
  scheduledProceduresWithPhysicianSubscription,
  scheduledProcedureWithPhysician,
  procedureStatisticsWithPhysician,
} from '../../../graph/procedures';
import EntityView from '@/se/components/entity/EntityView';
import QuestionnaireAnswers from './QuestionnaireAnswers';
import Filters from '../../pages/analytics/Filters';
import ProcedureEventLog, {
  filterNonIntakeEvents,
  procedureStatuseLabels,
  procedureStatuses,
} from './ProcedureEventLog';
import ProcedureInput from './ProcedureInput';
import { withTheme } from '@/se/theme';
import ScheduledProcedureMobileListItem from './components/ScheduledProcedureMobileListItem';
import PatientVisits from './PatientVisits';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Box from '@material-ui/core/Box';
import { SurgeryInstructions } from './instructions/SurgeryInstructions';
import { withSession } from '../../../state/Session';
import { unpackSessionObject } from '../../pages/unpackSessionObject';
import { RenderTitle } from './RenderTitle';
import { a11yProps, TabPanel } from './TabPanel';
import StatChips from './StatChips';
import useCareAppAccess from './utils/useCareAppAccess';
import Badge from '@material-ui/core/Badge';
import { NamedRange } from '../../core/DatePicker';
import scheduledProceduresSource from '../../../omnisearch/sources/hospital/scheduledProcedures';
import scheduledProceduresWithPhysicianSource from '../../../omnisearch/sources/hospital/scheduledProceduresWithPhysician';
import pick from 'lodash/fp/pick';
import Forms from './Forms';
import { useQuery } from '@apollo/client';
import { procedureForms } from '../../../graph/surgeon/forms';
// DescriptionTwoTone removed - no longer used with CompactDashboardFilter
import ProcedureNotes from './ProcedureNotes';
import { getLastProcedureEvent } from '../../../util/procedureEvents';
import { ProcedureStatus } from '../../../types/ProcedureEvent';
import { FormType } from '../../../types/Form';
import FileUploaderPreOp from '../../FileUploaderPreOp';

import { useScope } from '../../../hooks/useScope';
import { withScope } from '../../../contexts/ScopeContext';
import SelectionContextProvider from '../../../features/selection/SelectionContextProvider';
import { getSurgeryInstructionsDefaultTemplateType } from '../../../util/InstructionTemplatesService';
import identity from 'lodash/identity';

const Empty = withProps({
  title: `There are no scheduled procedures at the moment.`,
  hint: '',
  illustration: withTheme(theme => get(theme, 'illustration.patients')),
})(EntityEmptyState);

function tabSwitcher(state, action) {
  if (state.preopComplete) {
    switch (action.type) {
      case 'init':
        return { ...state, switchToTab: 1 };
      case 'formsExists':
        return state.formsLoaded
          ? state
          : action.formsExists
            ? { ...state, switchToTab: 2, formsLoaded: true }
            : { ...state, formsLoaded: true };
      default:
        return state;
    }
  } else {
    return state;
  }
}

const ProcedureAndQuestionnaire = props => {
  const { scope, accessToken, data, isPhysician, isSurgeonOfficeStaff } = props;
  useCareAppAccess(get(scope, 'hospital.id'), accessToken);
  const procedureId = data?.id;
  const notes = (data?.events || []).filter(event => event.type === 'Note');
  const notesCount = notes ? notes.length : 0;
  const procedureEvents = (data?.events || []).filter(event => event.type !== 'Note');
  const lastPreOpCompletedEvent = getLastProcedureEvent(data?.events || [], ProcedureStatus.PreOpCompleted);
  const questionnaireVersion = props?.data?.entryQuestionnaire?.questionnaireVersion;

  const {
    data: queryData,
    loading,
    error,
  } = useQuery(procedureForms, { variables: { procedureId, formType: FormType.PreOpForm } });
  const forms = (queryData?.procedureForms || []).filter(form =>
    form?.content?.includes('medicalPassport')
      ? (questionnaireVersion === 1 && form?.content === 'medicalPassport') ||
        `medicalPassport${questionnaireVersion}` === form?.content ||
        (form && form.content.startsWith('formsv2://'))
      : true
  );

  const formsExists = queryData?.procedureForms?.length > 0;

  const [tab, setTab] = useState(null);
  const tabNumber = tab || 0;

  const [{ switchToTab }, dispatch] = useReducer(tabSwitcher, {
    preopComplete: get(props, 'data.status') === procedureStatuses.preopCompleted,
  });

  useEffect(() => {
    dispatch({ type: 'init' });
  }, []);

  useEffect(() => {
    if (!loading && !error) {
      dispatch({ type: 'formsExists', formsExists });
    }
  }, [loading, error, formsExists]);

  useEffect(() => {
    if (switchToTab) {
      setTab(switchToTab);
    }
  }, [switchToTab]);

  const handleChange = (event, newValue) => {
    setTab(newValue);
  };
  const hospitalId = scope?.hospital?.id;

  const instructionLastSent = get(props.data, 'instructionLastSent', '{}');

  const [selectedTemplate, setSelectedTemplate] = useState(
    instructionLastSent?.type || getSurgeryInstructionsDefaultTemplateType(hospitalId)
  );

  return (
    <Fragment>
      <EntityView {...props} />
      {procedureId && <FileUploaderPreOp procedureId={procedureId} />}
      {formsExists && !isPhysician && !isSurgeonOfficeStaff && (
        <Box mt={6} minWidth={0}>
          <Tabs
            indicatorColor="primary"
            value={tabNumber}
            onChange={handleChange}
            aria-label="PreOP Patient Tabs"
            variant="scrollable"
          >
            <Tab label="PreOp Questionnaire" {...a11yProps(0)} />
            <Tab label="Forms" {...a11yProps(1)} />
            <Tab label="Instructions" {...a11yProps(2)} />
            <Tab label="Patient Visits" {...a11yProps(3)} />
            <Tab
              label={
                <Badge invisible={notesCount < 1} badgeContent={notesCount} color="primary">
                  PreOp Notes&nbsp;&nbsp;
                </Badge>
              }
              {...a11yProps(4)}
            />
            <Tab label="Procedure Events" {...a11yProps(5)} />
          </Tabs>
          {/* {procedureId && <PatientFileGenerator procedureId={procedureId} />} */}
          <TabPanel value={tabNumber} index={0}>
            <QuestionnaireAnswers
              entryQuestionnaire={get(props, 'data.entryQuestionnaire')}
              pacuCharts={get(props, 'data.pacuCharts')}
              procedure={get(props, 'data', {})}
            />
          </TabPanel>
          <TabPanel value={tabNumber} index={1}>
            <Forms procedureId={procedureId} forms={forms} lastPreOpCompletedEvent={lastPreOpCompletedEvent} />
          </TabPanel>
          <TabPanel value={tabNumber} index={2}>
            <SurgeryInstructions
              data={get(props, 'data', {})}
              selectedTemplate={selectedTemplate}
              onSelectedTemplate={setSelectedTemplate}
            />
          </TabPanel>
          <TabPanel value={tabNumber} index={3}>
            <PatientVisits
              visits={props?.data?.visits}
              currentVisit={props?.data?.id}
              baseUrlProvider={props.baseUrlProvider}
            />
          </TabPanel>
          <TabPanel value={tabNumber} index={4}>
            <ProcedureNotes procedureId={procedureId} procedureNotes={notes} />
          </TabPanel>
          <TabPanel value={tabNumber} index={5}>
            <ProcedureEventLog events={procedureEvents} name="Procedure Events" filterEvents={filterNonIntakeEvents} />
          </TabPanel>
        </Box>
      )}
      {!formsExists && !isPhysician && !isSurgeonOfficeStaff && (
        <Box mt={6}>
          <Tabs indicatorColor={'primary'} value={tabNumber} onChange={handleChange} aria-label="PerOp Patient Tabs">
            <Tab label="PreOp Questionnaire" {...a11yProps(0)} />
            <Tab label="Instructions" {...a11yProps(1)} />
            <Tab label="Patient Visits" {...a11yProps(2)} />
            <Tab
              label={
                <Badge invisible={notesCount < 1} badgeContent={notesCount} color="primary">
                  PreOp Notes&nbsp;&nbsp;
                </Badge>
              }
              {...a11yProps(3)}
            />
            <Tab label="Procedure Events" {...a11yProps(4)} />
          </Tabs>
          <TabPanel value={tabNumber} index={0}>
            <QuestionnaireAnswers
              entryQuestionnaire={get(props, 'data.entryQuestionnaire')}
              pacuCharts={get(props, 'data.pacuCharts')}
              procedure={get(props, 'data', {})}
            />
          </TabPanel>
          <TabPanel value={tabNumber} index={1}>
            <SurgeryInstructions
              data={get(props, 'data', {})}
              selectedTemplate={selectedTemplate}
              onSelectedTemplate={setSelectedTemplate}
            />
          </TabPanel>
          <TabPanel value={tabNumber} index={2}>
            <PatientVisits
              visits={get(props, 'data.visits')}
              currentVisit={get(props, 'data.id')}
              baseUrlProvider={props.baseUrlProvider}
            />
          </TabPanel>
          <TabPanel value={tabNumber} index={3}>
            <ProcedureNotes procedureId={procedureId} procedureNotes={notes} />
          </TabPanel>
          <TabPanel value={tabNumber} index={4}>
            <ProcedureEventLog events={procedureEvents} name="Procedure Events" filterEvents={filterNonIntakeEvents} />
          </TabPanel>
        </Box>
      )}
    </Fragment>
  );
};

const CustomFilter = withProps(props => ({
  textSearchKey: 'name',
  hideProcedureStatusSelectInput: false,
  hideCategorySelectInput: false, // Category is now a dropdown in the filter row
  hideSpecialitySelectInput: props.physicianIds.length > 0,
  hideProcedureTypeSelectInput: true,
  hidePhysicianSelectInput: props.physicianIds.length > 0,
  futurePicker: true,
  defaultValue: {
    dateRange: NamedRange.nextSevenDays(),
  },
  physicianIds: props.physicianIds,
  reload: false, // Disable page reload on filter changes
  dontReloadOn: ['name', 'physician', 'procedureType', 'speciality', 'status', 'dateRange', 'category'], // Never reload on these filters
}))(Filters);

/**
 * Stat items configuration for the header chips.
 */
const getStatItems = () => [
  {
    title: 'Scheduled',
    getter: data => get(data, 'procedureStatistics.scheduledProcedures', '-'),
  },
  {
    title: 'Submitted',
    getter: data => get(data, 'procedureStatistics.totalForms', '-'),
  },
  {
    title: 'PreOp Done',
    getter: data => get(data, 'procedureStatistics.preOpCompleted', '-'),
  },
  {
    title: 'Accepted',
    getter: data => get(data, 'procedureStatistics.instructionsAccepted', '-'),
  },
];

/**
 * Creates HeaderLeft render function for stat chips.
 * Stats are now displayed in the list header row alongside Print/Config icons.
 */
const createHeaderLeft =
  physicianIds =>
  ({ filter }) => (
    <StatChips
      subscription={physicianIds.length > 0 ? procedureStatisticsWithPhysician(physicianIds) : procedureStatistics}
      items={getStatItems()}
      filter={filter}
    />
  );

const ScheduledProceduresCRUD = (showColumns, physicianIds, hasPhysicianIds) =>
  GraphQLCRUD({
    entityName: 'ScheduledProcedure',
    scheme: {
      ...schema,
      list: hasPhysicianIds
        ? scheduledProceduresWithPhysicianSubscription(physicianIds)
        : schema.scheduledProceduresSubscription,
      item: hasPhysicianIds ? scheduledProcedureWithPhysician(physicianIds) : schema.scheduledProcedure,
    },
    List: {
      tableKey: 'ScheduledProcedure',
      MobileItemComponent: ScheduledProcedureMobileListItem,
      useColumnSelection: true,
      columns: listColumns,
      // All columns visible by default - users can hide via Configure Columns
      // Column order is optimized for ASC nurse workflow in columns.tsx
      Empty,
      FilterComponent: props => <CustomFilter {...props} physicianIds={physicianIds} />,
      HeaderLeft: createHeaderLeft(physicianIds),
      defaultFilterValues: {
        dateRange: NamedRange.nextSevenDays(),
        preOpRegistration: true,
      },
      defaultSort: { title: 'Date of Service', type: 'ASC' },
      pickFilter: pick([
        'name',
        'physician',
        'procedureType',
        'hospital',
        'status',
        'dateRange',
        'category',
        'speciality',
        'view',
        'preOpRegistration'
      ]),
      // Enable server-side pagination with IDs-based ECST subscription
      // Query for initial/paginated data, Subscription for real-time updates (only current page IDs)
      paginationConfig: hasPhysicianIds
        ? null // Keep full subscription for physician-filtered views (smaller datasets)
        : {
            query: schema.scheduledProceduresPaginatedQuery,
            countQuery: schema.scheduledProceduresCountQuery,

            // IDs-based subscription - only subscribes to items on current page
            // Much more efficient than full-list subscription for paginated views
            subscription: schema.proceduresByIdSubscription,
            subscriptionKey: 'proceduresById',

            entityKey: 'scheduledProceduresPaginated',
            countKey: 'scheduledProceduresCount',
            // pageSize defaults to 50 via DEFAULT_PAGE_SIZE
          },
    },
    Show: {
      columns: showColumns,
      View: compose(withSession(unpackSessionObject), withScope)(ProcedureAndQuestionnaire),
      Empty,
      Title: withProps({ procedureStatuses: procedureStatuseLabels })(RenderTitle),
    },
    ...(!hasPhysicianIds && {
      Edit: {
        Input: ProcedureInput,
        skipRefetch: true,
        mapEditItemProps: ({ data }) => ({
          data: {
            ...data,
            scheduledProcedure: {
              ...data?.scheduledProcedure,
              patientName: data?.scheduledProcedure?.patientNameUnformatted,
              patientDateOfBirth: data?.scheduledProcedure?.patientDateOfBirthISO,
            },
          },
        }),
      },
    }),
    searchSource: hasPhysicianIds ? scheduledProceduresWithPhysicianSource : scheduledProceduresSource,
  });

const emptyArray = [];

const ScheduledProcedures = props => {
  const scope = useScope();
  const createProcedureSchedule = scope?.hospital?.modules?.createProcedureSchedule;
  const organizationId = get2('hospital.id')(scope);
  const userMetadata = get2('session.user.metadata', props) || emptyArray;
  const memberships = get2('session.user.memberships', props) || emptyArray;
  const membership = memberships.find(m => m.organization.id === organizationId);
  const membershipMetadata = get2('metadata', membership) || emptyArray;
  const metadata = useMemo(() => [...userMetadata, ...membershipMetadata], [membershipMetadata, userMetadata]);

  const physicianIdsFromMetadata = useMemo(
    () =>
      metadata
        .filter(m => m.startsWith('Physician('))
        .map(physicianMetadata => {
          const match = physicianMetadata.match(/Physician\((\d+)\)/);
          return match ? parseInt(match[1], 10) : null;
        })
        .filter(id => id !== null),
    [metadata]
  );
  const hasPhysicianIds = physicianIdsFromMetadata?.length > 0;

  const CRUDComponent = ScheduledProceduresCRUD(
    createProcedureSchedule ? createdProcedureColumns : uploadedProcedureColumns,
    physicianIdsFromMetadata,
    hasPhysicianIds
  );

  return (
    <SelectionContextProvider>
      <CRUDComponent {...props} />
    </SelectionContextProvider>
  );
};

export default compose(withSession(identity), withScope)(ScheduledProcedures);
